import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, MapPin, LogOut, Search, Loader, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { adminAuth } from '../services/adminAuth';

interface Property {
  id: string;
  address: string;
  status: string;
  zillow_url?: string | null;
  original_image?: string | null;
  pencil_image_url: string | null;
  oil_image_url: string | null;
  charcoal_image_url: string | null;
  chalk_image_url: string | null;
  price?: string;
  score?: number;
  color?: string;
  prompt?: string;
  area?: string;
  created_at: string;
  user_id?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
}

interface BuyerAgentInfo {
  image_url_1?: string;
  image_url_2?: string;
  image_url_3?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip?: string;
  sold_date?: string;
  buyer_agent_name?: string;
  buyer_agent_license?: string;
  buyer_brokerage?: string;
  buyer_agent_phone?: string;
  ai_found_email?: string;
  ai_found_mobile?: string;
  search_method?: string;
  search_error?: string;
}

interface SoldProperty {
  id: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  price: string;
  soldDate: string;
  listingUrl: string;
  images: string[];
  agentInfo?: any;
  brokerageInfo?: any;
}

const AdminBuyerOutreach: React.FC = () => {
  const navigate = useNavigate();
  
  // Zillow scraping state
  const [zillowUrl, setZillowUrl] = useState('');
  const [scrapingLoading, setScrapingLoading] = useState(false);
  const [scrapingError, setScrapingError] = useState<string | null>(null);
  const [agentInfo, setAgentInfo] = useState<BuyerAgentInfo | null>(null);

  // Sold properties search state
  const [searchArea, setSearchArea] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [soldProperties, setSoldProperties] = useState<SoldProperty[]>([]);
  const [soldPropertiesLoading, setSoldPropertiesLoading] = useState(false);
  const [soldPropertiesError, setSoldPropertiesError] = useState<string | null>(null);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [paginationData, setPaginationData] = useState<any>(null);
  
  // Homes.com scraping state
  const [homesUrl, setHomesUrl] = useState('');
  const [homesLoading, setHomesLoading] = useState(false);
  const [homesError, setHomesError] = useState<string | null>(null);
  const [homesProperties, setHomesProperties] = useState<any>(null);
  
  // New state for detailed property information
  const [detailedProperties, setDetailedProperties] = useState<any[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsProgress, setDetailsProgress] = useState({ current: 0, total: 0 });
  const [detailsError, setDetailsError] = useState<string | null>(null);
  
  // Homes.com property selection and agent finding state
  const [selectedHomesProperties, setSelectedHomesProperties] = useState<Set<string>>(new Set());
  const [homesAgentFindingLoading, setHomesAgentFindingLoading] = useState(false);
  const [homesAgentFindingError, setHomesAgentFindingError] = useState<string | null>(null);
  const [homesFoundAgents, setHomesFoundAgents] = useState<any[]>([]);
  const [homesActiveTab, setHomesActiveTab] = useState<'all' | 'agents' | 'artwork'>('all');
  
  // Artwork generation state
  const [selectedAgentsForArtwork, setSelectedAgentsForArtwork] = useState<Set<string>>(new Set());
  const [artworkGenerationLoading, setArtworkGenerationLoading] = useState(false);
  const [artworkGenerationError, setArtworkGenerationError] = useState<string | null>(null);
  const [generatedArtwork, setGeneratedArtwork] = useState<any[]>([]);
  
  // New state for recent vs older properties
  const [recentProperties, setRecentProperties] = useState<SoldProperty[]>([]);
  const [olderProperties, setOlderProperties] = useState<SoldProperty[]>([]);
  const [lastPageProperties, setLastPageProperties] = useState<SoldProperty[]>([]);
  
  // Property selection and agent finding state
  const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set());
  const [agentFindingLoading, setAgentFindingLoading] = useState(false);
  const [agentFindingError, setAgentFindingError] = useState<string | null>(null);
  const [foundAgents, setFoundAgents] = useState<Array<SoldProperty & { agentInfo?: BuyerAgentInfo }>>([]);

  // Check authentication on component mount
  useEffect(() => {
    if (!adminAuth.isAuthenticated()) {
      navigate('/admin-login');
      return;
    }
  }, [navigate]);

  const handleCollectAgentInfo = async () => {
    if (!zillowUrl.trim()) {
      setScrapingError('Please enter a Zillow URL');
      return;
    }

    // Check if user is authenticated
    if (!adminAuth.isAuthenticated()) {
      setScrapingError('Please log in to access this feature');
      navigate('/admin-login');
      return;
    }

    try {
      setScrapingLoading(true);
      setScrapingError(null);
      setAgentInfo(null);

      const adminToken = adminAuth.getApiToken();
      console.log('Admin token:', adminToken ? `${adminToken.substring(0, 10)}...` : 'null');
      
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      // Use direct fetch instead of supabase.functions.invoke
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/collect-buyers-agent-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'x-admin-token': adminToken
        },
        body: JSON.stringify({ url: zillowUrl.trim() })
      });

      if (!response.ok) {
        console.error('Response not OK:', response.status, response.statusText);
        let errorData;
        try {
          errorData = await response.json();
          console.error('Error response data:', errorData);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
        }
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Success response data:', data);

      if (data.success && data.data?.aiExtractedData) {
        setAgentInfo(data.data.aiExtractedData);
      } else {
        throw new Error(data.message || 'No agent information found in the response');
      }

    } catch (err) {
      console.error('Error collecting agent info:', err);
      setScrapingError(err instanceof Error ? err.message : 'Failed to collect agent information');
    } finally {
      setScrapingLoading(false);
    }
  };

  const handleSearchSoldProperties = async () => {
    if (!searchArea.trim()) {
      setSoldPropertiesError('Please enter a search area');
      return;
    }

    // Check if user is authenticated
    if (!adminAuth.isAuthenticated()) {
      setSoldPropertiesError('Please log in to access this feature');
      navigate('/admin-login');
      return;
    }

    try {
      setSoldPropertiesLoading(true);
      setSoldPropertiesError(null);
      setSoldProperties([]);
      setRecentProperties([]);
      setOlderProperties([]);
      setLastPageProperties([]);
      setTotalResults(0);
      setTotalPages(0);
      setPaginationData(null);

      const adminToken = adminAuth.getApiToken();
      console.log('Admin token:', adminToken ? `${adminToken.substring(0, 10)}...` : 'null');
      
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      const response = await supabase.functions.invoke('scrape-sold-properties', {
        body: {
          searchArea,
          page: currentPage,
          priceMin: priceMin ? parseInt(priceMin) : undefined,
          priceMax: priceMax ? parseInt(priceMax) : undefined,
        },
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-Admin-Token': adminToken
        }
      });

      if (response.error) {
        console.error('Sold properties search error:', response.error);
        throw new Error(response.error.message || 'Failed to search sold properties');
      }

      if (response.data.success) {
        const properties = response.data.data?.listings || [];
        setSoldProperties(properties);
        setTotalResults(response.data.data?.totalResults || properties.length);
        setTotalPages(response.data.data?.totalPages || 1);
        setPaginationData(response.data.data?.pagination || null);
        
        // Separate recent and older properties
        const cutoffDate = new Date();
        cutoffDate.setMonth(cutoffDate.getMonth() - 6);
        
        const recent = properties.filter((prop: SoldProperty) => {
          const soldDate = new Date(prop.soldDate);
          return soldDate >= cutoffDate;
        });
        
        const older = properties.filter((prop: SoldProperty) => {
          const soldDate = new Date(prop.soldDate);
          return soldDate < cutoffDate;
        });
        
        setRecentProperties(recent);
        setOlderProperties(older);
        
        // If we have pagination data and it's not the last page, also get the last page
        if (response.data.data?.pagination?.hasNext && response.data.data?.pagination?.totalPages > 1) {
          try {
            const lastPageResponse = await supabase.functions.invoke('scrape-sold-properties', {
              body: {
                searchArea,
                page: response.data.data.pagination.totalPages,
                priceMin: priceMin ? parseInt(priceMin) : undefined,
                priceMax: priceMax ? parseInt(priceMax) : undefined,
              },
              headers: {
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                'X-Admin-Token': adminToken
              }
            });
            
            if (lastPageResponse.error) {
              console.error('Error fetching last page:', lastPageResponse.error);
            } else if (lastPageResponse.data.success) {
              setLastPageProperties(lastPageResponse.data.data?.listings || []);
            }
          } catch (lastPageError) {
            console.error('Error fetching last page:', lastPageError);
          }
        }
      } else {
        throw new Error(response.data.message || 'Failed to search properties');
      }
    } catch (error) {
      console.error('Error searching sold properties:', error);
      setSoldPropertiesError(error instanceof Error ? error.message : 'An error occurred while searching properties');
    } finally {
      setSoldPropertiesLoading(false);
    }
  };

  const handleSearchHomesProperties = async () => {
    if (!homesUrl.trim()) {
      setHomesError('Please enter a homes.com URL');
      return;
    }

    if (!homesUrl.includes('homes.com')) {
      setHomesError('Please enter a valid homes.com URL');
      return;
    }

    // Check if user is authenticated
    if (!adminAuth.isAuthenticated()) {
      setHomesError('Please log in to access this feature');
      navigate('/admin-login');
      return;
    }

    setHomesLoading(true);
    setHomesError(null);
    setHomesProperties(null);
    setDetailedProperties([]);
    setDetailsError(null);

    try {
      const adminToken = adminAuth.getApiToken();
      console.log('Admin token:', adminToken ? `${adminToken.substring(0, 10)}...` : 'null');
      
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      // Step 1: Get the property list
      console.log('Step 1: Getting property list...');
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scrape-homes-properties`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'X-Admin-Token': adminToken,
        },
        body: JSON.stringify({
          url: homesUrl,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to scrape homes.com properties');
      }

      setHomesProperties(data.data);
      
      // Step 2: Get detailed information for each property
      console.log('Step 2: Getting detailed property information...');
      const allProperties = [
        ...(data.data.firstPage?.properties || []),
        ...(data.data.lastPage?.properties || [])
      ];

      if (allProperties.length === 0) {
        console.log('No properties found to get details for');
        return;
      }

      setDetailsLoading(true);
      setDetailsProgress({ current: 0, total: allProperties.length });

      // Process properties in batches of 10
      const batchSize = 10;
      const detailedResults: any[] = [];
      
      for (let i = 0; i < allProperties.length; i += batchSize) {
        const batch = allProperties.slice(i, i + batchSize);
        console.log(`Processing batch ${Math.floor(i / batchSize) + 1}, properties ${i + 1}-${Math.min(i + batchSize, allProperties.length)}`);
        
        // Launch concurrent requests for this batch
        const batchPromises = batch.map(async (property: any) => {
          try {
            const detailResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scrape-property-details`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                'X-Admin-Token': adminToken,
              },
              body: JSON.stringify({
                url: property.href, // This will be converted to full URL by the function
              }),
            });

            if (!detailResponse.ok) {
              const errorData = await detailResponse.json().catch(() => ({}));
              throw new Error(errorData.message || `HTTP error! status: ${detailResponse.status}`);
            }

            const detailData = await detailResponse.json();
            
            if (detailData.success) {
              return {
                ...property,
                details: detailData.data
              };
            } else {
              console.error('Failed to get details for property:', property.href, detailData.message);
              return {
                ...property,
                details: null,
                error: detailData.message
              };
            }
          } catch (error) {
            console.error('Error getting details for property:', property.href, error);
            return {
              ...property,
              details: null,
              error: error instanceof Error ? error.message : 'Unknown error'
            };
          }
        });

        // Wait for all requests in this batch to complete
        const batchResults = await Promise.all(batchPromises);
        detailedResults.push(...batchResults);
        
        // Update progress
        setDetailsProgress({ current: detailedResults.length, total: allProperties.length });
      }

      setDetailedProperties(detailedResults);
      console.log('Completed getting detailed information for all properties');
      
    } catch (error) {
      console.error('Error scraping homes.com properties:', error);
      setHomesError(error instanceof Error ? error.message : 'An error occurred while scraping homes.com properties');
    } finally {
      setHomesLoading(false);
      setDetailsLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    // Trigger search with new page
    setTimeout(() => {
      handleSearchSoldProperties();
    }, 100);
  };

  const handlePropertySelection = (propertyId: string, isSelected: boolean) => {
    const newSelected = new Set(selectedProperties);
    if (isSelected) {
      newSelected.add(propertyId);
    } else {
      newSelected.delete(propertyId);
    }
    setSelectedProperties(newSelected);
  };

  const handleSelectAllProperties = (properties: SoldProperty[], isSelected: boolean) => {
    const newSelected = new Set(selectedProperties);
    properties.forEach(property => {
      if (isSelected) {
        newSelected.add(property.id);
      } else {
        newSelected.delete(property.id);
      }
    });
    setSelectedProperties(newSelected);
  };

  const handleFindAgents = async () => {
    if (selectedProperties.size === 0) {
      setAgentFindingError('Please select at least one property');
      return;
    }

    try {
      setAgentFindingLoading(true);
      setAgentFindingError(null);
      const newFoundAgents: Array<SoldProperty & { agentInfo?: BuyerAgentInfo }> = [];

      // Get all selected properties from both lists
      const allProperties = [...recentProperties, ...olderProperties];
      const selectedPropertiesData = allProperties.filter(property => 
        selectedProperties.has(property.id)
      );

      // Process each selected property
      for (const property of selectedPropertiesData) {
        try {
          console.log(`Finding agent for property: ${property.address}`);
          
          const adminToken = adminAuth.getApiToken();
          console.log('Admin token:', adminToken ? `${adminToken.substring(0, 10)}...` : 'null');
          
          if (!adminToken) {
            throw new Error('Admin authentication required');
          }
          
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/collect-buyers-agent-info`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
              'authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'x-admin-token': adminToken
            },
            body: JSON.stringify({ url: property.listingUrl })
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error(`Error finding agent for ${property.address}:`, errorData);
            newFoundAgents.push({
              ...property,
              agentInfo: {
                search_error: errorData.error || errorData.message || `HTTP error! status: ${response.status}`
              }
            });
          } else {
            const data = await response.json();
            
            if (data.success && data.data?.aiExtractedData) {
              newFoundAgents.push({
                ...property,
                agentInfo: data.data.aiExtractedData
              });
            } else {
              newFoundAgents.push({
                ...property,
                agentInfo: {
                  search_error: data.message || 'No agent information found'
                }
              });
            }
          }
        } catch (err) {
          console.error(`Error processing property ${property.address}:`, err);
          newFoundAgents.push({
            ...property,
            agentInfo: {
              search_error: err instanceof Error ? err.message : 'Unknown error'
            }
          });
        }
      }

      setFoundAgents(newFoundAgents);
      
    } catch (err) {
      console.error('Error finding agents:', err);
      setAgentFindingError(err instanceof Error ? err.message : 'Failed to find agents');
    } finally {
      setAgentFindingLoading(false);
    }
  };

  // Homes.com property selection handlers
  const handleHomesPropertySelection = (propertyIndex: number, isSelected: boolean) => {
    const newSelected = new Set(selectedHomesProperties);
    if (isSelected) {
      newSelected.add(propertyIndex.toString());
    } else {
      newSelected.delete(propertyIndex.toString());
    }
    setSelectedHomesProperties(newSelected);
  };

  const handleSelectAllHomesProperties = (isSelected: boolean) => {
    const newSelected = new Set(selectedHomesProperties);
    if (isSelected) {
      detailedProperties.forEach((_, index) => {
        newSelected.add(index.toString());
      });
    } else {
      newSelected.clear();
    }
    setSelectedHomesProperties(newSelected);
  };

  const handleFindHomesAgents = async () => {
    if (selectedHomesProperties.size === 0) {
      setHomesAgentFindingError('Please select at least one property');
      return;
    }

    try {
      setHomesAgentFindingLoading(true);
      setHomesAgentFindingError(null);
      const newFoundAgents: any[] = [];

      // Get selected properties
      const selectedPropertiesData = detailedProperties.filter((_, index) => 
        selectedHomesProperties.has(index.toString())
      );

      console.log(`Processing ${selectedPropertiesData.length} selected properties for agent finding`);

      // Process each selected property
      for (const property of selectedPropertiesData) {
        try {
          console.log(`Finding agent details for homes.com property:`, property);
          
          const adminToken = adminAuth.getApiToken();
          
          if (!adminToken) {
            throw new Error('Admin authentication required');
          }

          // Extract agent information from the property details
          const buyerAgent = property.details?.lastBuyersAgent;
          const sellingAgent = property.details?.lastSellingAgent;
          
          console.log('Buyer agent from property details:', buyerAgent);
          console.log('Selling agent from property details:', sellingAgent);
          
          // Only search for buyer's agent contact information
          if (!buyerAgent?.name) {
            console.log(`No buyer agent name found for property: ${property.title}`);
            newFoundAgents.push({
              ...property,
              agentInfo: {
                searchError: 'No buyer agent information found in property details'
              }
            });
            continue;
          }

          console.log(`Searching for contact details for buyer agent: ${buyerAgent.name}`);

          // Add a timeout to the fetch request
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

          try {
            // Use the new find-agent-details function for buyer's agent only
            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/find-agent-details`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                'authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                'x-admin-token': adminToken
              },
              body: JSON.stringify({
                agentName: buyerAgent.name,
                agentLicense: buyerAgent.license,
                brokerage: buyerAgent.broker,
                city: property.details?.city,
                state: property.details?.state
              }),
              signal: controller.signal
            });

            clearTimeout(timeoutId);

            console.log(`Response received for ${buyerAgent.name}:`, response.status);

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              console.error(`Error finding agent details for property:`, errorData);
              newFoundAgents.push({
                ...property,
                agentInfo: {
                  searchError: errorData.error || errorData.message || `HTTP error! status: ${response.status}`
                }
              });
            } else {
              const data = await response.json();
              console.log(`Agent details response for ${buyerAgent.name}:`, data);
              
              if (data.success && data.data) {
                // Map the response to match the expected format
                newFoundAgents.push({
                  ...property,
                  agentInfo: {
                    buyer_agent_name: data.data.agentName,
                    buyer_agent_license: data.data.agentLicense,
                    buyer_brokerage: data.data.brokerage,
                    ai_found_email: data.data.aiFoundEmail,
                    ai_found_phone: data.data.aiFoundPhone,
                    ai_found_mobile: data.data.aiFoundMobile,
                    search_method: data.data.searchMethod,
                    confidence: data.data.confidence,
                    notes: data.data.notes
                  }
                });
              } else {
                newFoundAgents.push({
                  ...property,
                  agentInfo: {
                    searchError: data.error || 'Failed to find agent contact details'
                  }
                });
              }
            }
          } catch (fetchError) {
            clearTimeout(timeoutId);
            if (fetchError instanceof Error && fetchError.name === 'AbortError') {
              console.error(`Request timeout for agent ${buyerAgent.name}`);
              newFoundAgents.push({
                ...property,
                agentInfo: {
                  searchError: 'Request timeout - agent search took too long'
                }
              });
            } else {
              throw fetchError;
            }
          }
        } catch (err) {
          console.error(`Error processing property:`, err);
          newFoundAgents.push({
            ...property,
            agentInfo: {
              searchError: err instanceof Error ? err.message : 'Unknown error'
            }
          });
        }
      }

      console.log(`Completed processing all properties. Found ${newFoundAgents.length} results.`);
      setHomesFoundAgents(newFoundAgents);
      setHomesActiveTab('agents'); // Switch to agents tab after finding agents
      
    } catch (err) {
      console.error('Error finding homes.com agents:', err);
      setHomesAgentFindingError(err instanceof Error ? err.message : 'Failed to find agents');
    } finally {
      setHomesAgentFindingLoading(false);
    }
  };

  // Artwork generation handlers
  const handleAgentSelectionForArtwork = (agentIndex: number, isSelected: boolean) => {
    const newSelected = new Set(selectedAgentsForArtwork);
    if (isSelected) {
      newSelected.add(agentIndex.toString());
    } else {
      newSelected.delete(agentIndex.toString());
    }
    setSelectedAgentsForArtwork(newSelected);
  };

  const handleSelectAllAgentsForArtwork = (isSelected: boolean) => {
    const newSelected = new Set(selectedAgentsForArtwork);
    if (isSelected) {
      homesFoundAgents.forEach((_, index) => {
        newSelected.add(index.toString());
      });
    } else {
      newSelected.clear();
    }
    setSelectedAgentsForArtwork(newSelected);
  };

  const handleGenerateArtwork = async () => {
    if (selectedAgentsForArtwork.size === 0) {
      setArtworkGenerationError('Please select at least one property to generate artwork for');
      return;
    }

    // Get admin token for authentication
    const adminToken = adminAuth.getApiToken();
    console.log('Admin token:', adminToken ? `${adminToken.substring(0, 10)}...` : 'null');

    if (!adminToken) {
      setArtworkGenerationError('Authentication required. Please log in again.');
      return;
    }

    try {
      setArtworkGenerationLoading(true);
      setArtworkGenerationError(null);
      const newGeneratedArtwork: any[] = [];

      // Get selected agents
      const selectedAgentsData = homesFoundAgents.filter((_, index) => 
        selectedAgentsForArtwork.has(index.toString())
      );

      // Process each selected property
      for (const agentProperty of selectedAgentsData) {
        try {
          console.log(`Generating artwork for property:`, agentProperty);
          
          // Extract address from property details - build most complete address possible
          let address = '';
          
          // Try to build a complete address from detailed components
          if (agentProperty.details?.streetNumber && agentProperty.details?.streetName) {
            address = `${agentProperty.details.streetNumber} ${agentProperty.details.streetName}`;
            
            // Add city, state, zip if available
            const locationParts = [];
            if (agentProperty.details.city) locationParts.push(agentProperty.details.city);
            if (agentProperty.details.state) locationParts.push(agentProperty.details.state);
            if (agentProperty.details.zipCode) locationParts.push(agentProperty.details.zipCode);
            
            if (locationParts.length > 0) {
              address += `, ${locationParts.join(', ')}`;
            }
          } else if (agentProperty.details?.fullStreetAddress) {
            // Use full street address and add location if missing
            address = agentProperty.details.fullStreetAddress;
            
            // Check if city/state/zip are missing and add them
            const locationParts = [];
            if (agentProperty.details.city && !address.includes(agentProperty.details.city)) {
              locationParts.push(agentProperty.details.city);
            }
            if (agentProperty.details.state && !address.includes(agentProperty.details.state)) {
              locationParts.push(agentProperty.details.state);
            }
            if (agentProperty.details.zipCode && !address.includes(agentProperty.details.zipCode)) {
              locationParts.push(agentProperty.details.zipCode);
            }
            
            if (locationParts.length > 0) {
              address += `, ${locationParts.join(', ')}`;
            }
          } else {
            // Fallback to href or default
            address = agentProperty.href || 'Property';
          }

          if (!address || address === 'Property') {
            console.log('No address found for property');
            newGeneratedArtwork.push({
              ...agentProperty,
              artworkError: 'No address available for artwork generation'
            });
            continue;
          }

          // Use the new generate-artwork-webhook function
          const artworkResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-artwork-webhook`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
              'authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'X-Admin-Token': adminToken,
            },
            body: JSON.stringify({
              address: address,
              style: 'watercolor',
              request_id: `artwork_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            })
          });

          if (!artworkResponse.ok) {
            const errorData = await artworkResponse.json().catch(() => ({}));
            console.error(`Error generating artwork:`, errorData);
            newGeneratedArtwork.push({
              ...agentProperty,
              artworkError: errorData.error || `HTTP error! status: ${artworkResponse.status}`
            });
          } else {
            const artworkData = await artworkResponse.json();
            
            if (artworkData.success && artworkData.data?.artworkUrl) {
              newGeneratedArtwork.push({
                ...agentProperty,
                artworkUrl: artworkData.data.artworkUrl,
                artworkGenerated: true,
                artworkStyle: artworkData.data.style,
                artworkRequestId: artworkData.data.request_id
              });
            } else {
              newGeneratedArtwork.push({
                ...agentProperty,
                artworkError: artworkData.error || 'Failed to generate artwork'
              });
            }
          }
        } catch (err) {
          console.error(`Error processing artwork for property:`, err);
          newGeneratedArtwork.push({
            ...agentProperty,
            artworkError: `Failed to generate artwork: ${err instanceof Error ? err.message : 'Unknown error'}`
          });
        }
      }

      setGeneratedArtwork(newGeneratedArtwork);
      setHomesActiveTab('artwork'); // Switch to artwork tab after generation
      
    } catch (err) {
      console.error('Error generating artwork:', err);
      setArtworkGenerationError(err instanceof Error ? err.message : 'Failed to generate artwork');
    } finally {
      setArtworkGenerationLoading(false);
    }
  };

  const handleSignOut = () => {
    adminAuth.logout();
    navigate('/admin-login');
  };

  // Helper function to create a placeholder image as data URL
  const createPlaceholderImage = (property: any): string => {
    // Create a simple colored rectangle as a placeholder
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 600;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      // Return a simple fallback data URL
      return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
    }
    
    // Create a gradient background
    const gradient = ctx.createLinearGradient(0, 0, 800, 600);
    gradient.addColorStop(0, '#e3f2fd');
    gradient.addColorStop(1, '#bbdefb');
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 800, 600);
    
    // Add some text
    ctx.fillStyle = '#1565c0';
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Property Image', 400, 250);
    
    ctx.font = '24px Arial';
    ctx.fillText('Unavailable', 400, 300);
    
    ctx.font = '16px Arial';
    ctx.fillStyle = '#666';
    ctx.fillText('Original image could not be accessed', 400, 350);
    ctx.fillText('Generating artwork from placeholder', 400, 380);
    
    // Convert canvas to data URL
    return canvas.toDataURL('image/png');
  };

  if (!adminAuth.isAuthenticated()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-6 h-6" />
              Buyer Outreach
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleSignOut}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <LogOut className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Search Sold Properties */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <List className="w-5 h-5" />
            Search Sold Properties
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label htmlFor="search-area" className="block text-sm font-medium text-gray-700 mb-2">
                Search Area
              </label>
              <input
                type="text"
                id="search-area"
                value={searchArea}
                onChange={(e) => setSearchArea(e.target.value)}
                placeholder="New York, NY"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={soldPropertiesLoading}
              />
            </div>
            
            <div>
              <label htmlFor="page-number" className="block text-sm font-medium text-gray-700 mb-2">
                Page Number
              </label>
              <input
                type="number"
                id="page-number"
                value={currentPage}
                onChange={(e) => setCurrentPage(parseInt(e.target.value) || 1)}
                placeholder="1"
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={soldPropertiesLoading}
              />
            </div>
            
            <div>
              <label htmlFor="price-min" className="block text-sm font-medium text-gray-700 mb-2">
                Min Price
              </label>
              <input
                type="number"
                id="price-min"
                value={priceMin}
                onChange={(e) => setPriceMin(e.target.value)}
                placeholder="1500000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={soldPropertiesLoading}
              />
            </div>
            
            <div>
              <label htmlFor="price-max" className="block text-sm font-medium text-gray-700 mb-2">
                Max Price
              </label>
              <input
                type="number"
                id="price-max"
                value={priceMax}
                onChange={(e) => setPriceMax(e.target.value)}
                placeholder="3000000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={soldPropertiesLoading}
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={handleSearchSoldProperties}
              disabled={soldPropertiesLoading || !searchArea.trim()}
              className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {soldPropertiesLoading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              {soldPropertiesLoading ? 'Searching...' : 'Search Properties'}
            </button>
            
            {soldProperties.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1 || soldPropertiesLoading}
                  className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} {totalPages > 0 ? `of ${totalPages}` : ''} • {soldProperties.length} results
                  {totalResults > soldProperties.length && (
                    <span className="text-gray-500"> (showing {soldProperties.length} of {totalResults} total)</span>
                  )}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={soldPropertiesLoading || (totalPages > 0 && currentPage >= totalPages)}
                  className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          
          {soldPropertiesError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{soldPropertiesError}</p>
            </div>
          )}

          {soldProperties.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm text-blue-700">
                <div className="font-medium mb-1">Search Results for "{searchArea}"</div>
                <div className="text-blue-600">
                  Showing page {currentPage} of {totalPages} pages • 
                  {soldProperties.length} properties on this page
                  {totalResults > soldProperties.length && (
                    <span> • {totalResults.toLocaleString()} total properties found</span>
                  )}
                  {priceMin || priceMax ? (
                    <span> • Price range: ${priceMin ? parseInt(priceMin).toLocaleString() : '0'} - ${priceMax ? parseInt(priceMax).toLocaleString() : '∞'}</span>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* Agent Finding Controls */}
          {(recentProperties.length > 0 || olderProperties.length > 0) && (
            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-medium text-gray-900">Find Buyer Agents</h4>
                  <p className="text-sm text-gray-600">
                    Select properties to find their buyer agents ({selectedProperties.size} selected)
                  </p>
                </div>
                <button
                  onClick={handleFindAgents}
                  disabled={agentFindingLoading || selectedProperties.size === 0}
                  className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {agentFindingLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  {agentFindingLoading ? 'Finding Agents...' : 'Find Agents'}
                </button>
              </div>
              
              {agentFindingError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{agentFindingError}</p>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Search Homes.com Properties */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Search Homes.com Properties
          </h3>
          
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label htmlFor="homes-url" className="block text-sm font-medium text-gray-700 mb-2">
                Homes.com Search URL
              </label>
              <input
                type="url"
                id="homes-url"
                value={homesUrl}
                onChange={(e) => setHomesUrl(e.target.value)}
                placeholder="https://www.homes.com/new-york-ny/sold/"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={homesLoading}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleSearchHomesProperties}
                disabled={homesLoading || !homesUrl.trim()}
                className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {homesLoading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                {homesLoading ? 'Searching...' : 'Search Homes.com'}
              </button>
            </div>
          </div>
          
          {homesError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{homesError}</p>
            </div>
          )}

          {homesProperties && Object.keys(homesProperties).length > 0 && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-sm text-green-700">
                <div className="font-medium mb-1">Homes.com Search Results</div>
                <div className="text-green-600">
                  Found {homesProperties.summary?.totalPropertiesFound || 0} properties
                  {homesProperties.summary?.firstPageCount && (
                    <span> • {homesProperties.summary.firstPageCount} from first page</span>
                  )}
                  {homesProperties.summary?.lastPageCount && (
                    <span> • {homesProperties.summary.lastPageCount} from last page</span>
                  )}
                  {homesProperties.pagination?.totalPages && (
                    <span> • {homesProperties.pagination.totalPages} total pages</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Progress Bar for Details Loading */}
          {detailsLoading && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-blue-800">
                  Getting Detailed Property Information...
                </div>
                <div className="text-sm text-blue-600">
                  {detailsProgress.current} of {detailsProgress.total}
                </div>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${detailsProgress.total > 0 ? (detailsProgress.current / detailsProgress.total) * 100 : 0}%`
                  }}
                ></div>
              </div>
              <div className="mt-2 text-xs text-blue-600">
                Processing property details in batches... This may take a few moments.
              </div>
            </div>
          )}

          {detailsError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{detailsError}</p>
            </div>
          )}

          {/* Display Detailed Properties */}
          {detailedProperties.length > 0 && (
            <div className="mt-4">
              {/* Tab Navigation */}
              <div className="flex border-b border-gray-200 mb-4">
                <button
                  onClick={() => setHomesActiveTab('all')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ${
                    homesActiveTab === 'all'
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  All Entries ({detailedProperties.length})
                </button>
                <button
                  onClick={() => setHomesActiveTab('agents')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ml-8 ${
                    homesActiveTab === 'agents'
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Agents Assigned ({homesFoundAgents.length})
                </button>
                <button
                  onClick={() => setHomesActiveTab('artwork')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ml-8 ${
                    homesActiveTab === 'artwork'
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Artwork Generated ({generatedArtwork.length})
                </button>
              </div>

              {/* All Entries Tab */}
              {homesActiveTab === 'all' && (
                <div>
                  {/* Selection Controls */}
                  <div className="flex items-center justify-between mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="select-all-homes"
                          checked={detailedProperties.length > 0 && detailedProperties.every((_, index) => selectedHomesProperties.has(index.toString()))}
                          onChange={(e) => handleSelectAllHomesProperties(e.target.checked)}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <label htmlFor="select-all-homes" className="text-sm text-gray-700">
                          Select All
                        </label>
                      </div>
                      <div className="text-sm text-gray-600">
                        {selectedHomesProperties.size} of {detailedProperties.length} properties selected - Find contact info for buyer's agents
                      </div>
                    </div>
                    <button
                      onClick={handleFindHomesAgents}
                      disabled={homesAgentFindingLoading || selectedHomesProperties.size === 0}
                      className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {homesAgentFindingLoading ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                      {homesAgentFindingLoading ? 'Finding Agents...' : 'Find Buyer Agent Contacts'}
                    </button>
                  </div>

                  {homesAgentFindingError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{homesAgentFindingError}</p>
                    </div>
                  )}

                  {/* Properties List */}
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="divide-y divide-gray-200">
                      {detailedProperties.map((property: any, index: number) => (
                        <div key={index} className="p-4 hover:bg-gray-50">
                          <div className="flex items-center gap-4">
                            <input
                              type="checkbox"
                              id={`homes-property-${index}`}
                              checked={selectedHomesProperties.has(index.toString())}
                              onChange={(e) => handleHomesPropertySelection(index, e.target.checked)}
                              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 flex-shrink-0"
                            />
                            
                            {/* Property Image */}
                            {property.details?.primaryImageUrl ? (
                              <img
                                src={property.details.primaryImageUrl}
                                alt={`Property ${index + 1}`}
                                className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                <span className="text-gray-400 text-xs">No Image</span>
                              </div>
                            )}
                            
                            {/* Property Details */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-medium text-gray-900 mb-1">
                                  Property {index + 1}
                                </div>
                                {property.details?.dateSold && (
                                  <div className="text-sm text-gray-500">
                                    Sold: {property.details.dateSold}
                                  </div>
                                )}
                              </div>
                              
                              {/* Detailed Address Information */}
                              {(property.details?.fullStreetAddress || property.details?.streetNumber || property.details?.streetName) && (
                                <div className="mb-3 p-2 bg-blue-50 rounded text-sm">
                                  <div className="font-medium text-blue-800 mb-1">Property Address</div>
                                  {property.details.fullStreetAddress ? (
                                    <div className="text-blue-700">{property.details.fullStreetAddress}</div>
                                  ) : (
                                    <div className="text-blue-700">
                                      {property.details.streetNumber && property.details.streetName && 
                                        `${property.details.streetNumber} ${property.details.streetName}`
                                      }
                                    </div>
                                  )}
                                  
                                  {(property.details.city || property.details.state || property.details.zipCode) && (
                                    <div className="text-blue-600 text-xs">
                                      {[property.details.city, property.details.state, property.details.zipCode].filter(Boolean).join(', ')}
                                    </div>
                                  )}
                                  
                                  {property.details.county && (
                                    <div className="text-blue-600 text-xs">County: {property.details.county}</div>
                                  )}
                                  
                                  {property.details.neighborhood && (
                                    <div className="text-blue-600 text-xs">Neighborhood: {property.details.neighborhood}</div>
                                  )}
                                </div>
                              )}

                              {/* Property Information */}
                              {property.details?.salePrice && (
                                <div className="mb-3 p-2 bg-gray-50 rounded text-sm">
                                  <div className="font-medium text-gray-800 mb-1">Property Details</div>
                                  <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                                    <span className="font-medium text-green-700">Sold: {property.details.salePrice}</span>
                                  </div>
                                </div>
                              )}
                              
                              <div className="text-sm text-gray-600 mb-3">
                                {property.details?.url ? (
                                  <a 
                                    href={property.details.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline"
                                  >
                                    View Property →
                                  </a>
                                ) : (
                                  <span>Path: {property.href}</span>
                                )}
                              </div>

                              {/* Agent Information Summary */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="text-sm">
                                  {property.details?.lastSellingAgent?.name && (
                                    <div className="mb-2">
                                      <span className="font-medium text-green-800">Listing Agent:</span>
                                      <div className="text-gray-700">{property.details.lastSellingAgent.name}</div>
                                      {property.details.lastSellingAgent.broker && (
                                        <div className="text-gray-600 text-xs">{property.details.lastSellingAgent.broker}</div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {property.details?.lastBuyersAgent?.name && (
                                    <div className="mb-2">
                                      <span className="font-medium text-blue-800">Buyer's Agent:</span>
                                      <div className="text-gray-700">{property.details.lastBuyersAgent.name}</div>
                                      {property.details.lastBuyersAgent.broker && (
                                        <div className="text-gray-600 text-xs">{property.details.lastBuyersAgent.broker}</div>
                                      )}
                                    </div>
                                  )}
                                </div>

                                <div className="text-sm">
                                  {property.agentInfo && !property.agentInfo.searchError && (
                                    <div>
                                      <span className="font-medium text-blue-800">Contact Info:</span>
                                      {property.agentInfo.ai_found_email && (
                                        <div className="text-gray-700">
                                          <a href={`mailto:${property.agentInfo.ai_found_email}`} className="text-blue-600 hover:text-blue-800">
                                            {property.agentInfo.ai_found_email}
                                          </a>
                                        </div>
                                      )}
                                      {property.agentInfo.ai_found_phone && (
                                        <div className="text-gray-700">
                                          <a href={`tel:${property.agentInfo.ai_found_phone}`} className="text-blue-600 hover:text-blue-800">
                                            {property.agentInfo.ai_found_phone}
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Artwork Status */}
                              {property.artworkGenerated && (
                                <div className="mt-3 flex items-center gap-2">
                                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                  <span className="text-sm text-green-600 font-medium">Watercolor artwork generated successfully</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Agents Tab */}
              {homesActiveTab === 'agents' && (
                <div>
                  {homesFoundAgents.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No agents found yet. Select properties from the "All Entries" tab and click "Find Agents" to get started.
                    </div>
                  ) : (
                    <div>
                      {/* Artwork Generation Controls */}
                      <div className="flex items-center justify-between mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="select-all-agents-artwork"
                              checked={homesFoundAgents.length > 0 && homesFoundAgents.every((_, index) => selectedAgentsForArtwork.has(index.toString()))}
                              onChange={(e) => handleSelectAllAgentsForArtwork(e.target.checked)}
                              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                            />
                            <label htmlFor="select-all-agents-artwork" className="text-sm text-gray-700">
                              Select All
                            </label>
                          </div>
                          <div className="text-sm text-gray-600">
                            {selectedAgentsForArtwork.size} of {homesFoundAgents.length} properties selected for artwork generation
                          </div>
                        </div>
                        <button
                          onClick={handleGenerateArtwork}
                          disabled={artworkGenerationLoading || selectedAgentsForArtwork.size === 0}
                          className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {artworkGenerationLoading ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <Search className="w-4 h-4" />
                          )}
                          {artworkGenerationLoading ? 'Generating Artwork...' : 'Generate Artwork'}
                        </button>
                      </div>

                      {artworkGenerationError && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-600">{artworkGenerationError}</p>
                        </div>
                      )}

                      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="divide-y divide-gray-200">
                          {homesFoundAgents.map((property: any, index: number) => (
                            <div key={index} className="p-4">
                              <div className="flex items-start gap-4">
                                {/* Selection Checkbox */}
                                <input
                                  type="checkbox"
                                  id={`agent-artwork-${index}`}
                                  checked={selectedAgentsForArtwork.has(index.toString())}
                                  onChange={(e) => handleAgentSelectionForArtwork(index, e.target.checked)}
                                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 flex-shrink-0 mt-1"
                                />
                                
                                {/* Property Image */}
                                {property.details?.primaryImageUrl ? (
                                  <img
                                    src={property.details.primaryImageUrl}
                                    alt={`Property ${index + 1}`}
                                    className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <span className="text-gray-400 text-xs">No Image</span>
                                  </div>
                                )}
                                
                                {/* Property and Agent Details */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="text-sm font-medium text-gray-900">
                                      Property {index + 1}
                                    </div>
                                    {property.details?.dateSold && (
                                      <div className="text-sm text-gray-500">
                                        Sold: {property.details.dateSold}
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="text-sm text-gray-600 mb-3">
                                    {property.details?.url ? (
                                      <a 
                                        href={property.details.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 underline"
                                      >
                                        View Property →
                                      </a>
                                    ) : (
                                      <span>Path: {property.href}</span>
                                    )}
                                  </div>

                                  {/* Agent Information */}
                                  {property.agentInfo?.searchError ? (
                                    <div className="p-3 bg-red-50 rounded-lg">
                                      <div className="font-medium text-red-800 mb-1">Error</div>
                                      <div className="text-sm text-red-700">{property.agentInfo.searchError}</div>
                                    </div>
                                  ) : property.agentInfo ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="p-3 bg-gray-50 rounded-lg">
                                        <div className="font-medium text-gray-900 mb-2">Agent Information</div>
                                        <div className="text-sm text-gray-600 space-y-2">
                                          {/* Listing Agent */}
                                          {property.details?.lastSellingAgent?.name && (
                                            <div className="p-2 bg-green-50 rounded border-l-4 border-green-400">
                                              <div className="font-medium text-green-800">Listing Agent</div>
                                              <div className="text-sm text-green-700">
                                                <div><span className="font-medium">Name:</span> {property.details.lastSellingAgent.name}</div>
                                                {property.details.lastSellingAgent.broker && (
                                                  <div><span className="font-medium">Brokerage:</span> {property.details.lastSellingAgent.broker}</div>
                                                )}
                                                {property.details.lastSellingAgent.license && (
                                                  <div><span className="font-medium">License:</span> {property.details.lastSellingAgent.license}</div>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                          
                                          {/* Buyer's Agent */}
                                          {property.details?.lastBuyersAgent?.name && (
                                            <div className="p-2 bg-blue-50 rounded border-l-4 border-blue-400">
                                              <div className="font-medium text-blue-800">Buyer's Agent</div>
                                              <div className="text-sm text-blue-700">
                                                <div><span className="font-medium">Name:</span> {property.details.lastBuyersAgent.name}</div>
                                                {property.details.lastBuyersAgent.broker && (
                                                  <div><span className="font-medium">Brokerage:</span> {property.details.lastBuyersAgent.broker}</div>
                                                )}
                                                {property.details.lastBuyersAgent.license && (
                                                  <div><span className="font-medium">License:</span> {property.details.lastBuyersAgent.license}</div>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                          
                                          {property.agentInfo.confidence && (
                                            <div>
                                              <span className="font-medium">Search Confidence:</span> 
                                              <span className={`ml-1 px-2 py-1 rounded text-xs ${
                                                property.agentInfo.confidence === 'high' ? 'bg-green-100 text-green-800' :
                                                property.agentInfo.confidence === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-red-100 text-red-800'
                                              }`}>
                                                {property.agentInfo.confidence}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="p-3 bg-gray-50 rounded-lg">
                                        <div className="font-medium text-gray-900 mb-2">Buyer's Agent Contact Info</div>
                                        <div className="text-sm text-gray-600 space-y-1">
                                          {property.agentInfo.ai_found_email && (
                                            <div>
                                              <span className="font-medium">Email:</span> 
                                              <a href={`mailto:${property.agentInfo.ai_found_email}`} className="text-blue-600 hover:text-blue-800 ml-1">
                                                {property.agentInfo.ai_found_email}
                                              </a>
                                            </div>
                                          )}
                                          {property.agentInfo.ai_found_phone && (
                                            <div>
                                              <span className="font-medium">Phone:</span> 
                                              <a href={`tel:${property.agentInfo.ai_found_phone}`} className="text-blue-600 hover:text-blue-800 ml-1">
                                                {property.agentInfo.ai_found_phone}
                                              </a>
                                            </div>
                                          )}
                                          {property.agentInfo.ai_found_mobile && (
                                            <div>
                                              <span className="font-medium">Mobile:</span> 
                                              <a href={`tel:${property.agentInfo.ai_found_mobile}`} className="text-blue-600 hover:text-blue-800 ml-1">
                                                {property.agentInfo.ai_found_mobile}
                                              </a>
                                            </div>
                                          )}
                                          {property.agentInfo.search_method && (
                                            <div>
                                              <span className="font-medium">Search Method:</span> 
                                              <span className="ml-1 px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                                                {property.agentInfo.search_method}
                                              </span>
                                            </div>
                                          )}
                                          {property.agentInfo.notes && (
                                            <div>
                                              <span className="font-medium">Notes:</span> 
                                              <span className="ml-1 text-gray-700">{property.agentInfo.notes}</span>
                                            </div>
                                          )}
                                          {!property.agentInfo.ai_found_email && !property.agentInfo.ai_found_phone && !property.agentInfo.ai_found_mobile && (
                                            <div className="text-sm text-gray-500">No contact information found</div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="space-y-2">
                                      {/* Show basic agent info even without contact search */}
                                      {property.details?.lastSellingAgent?.name && (
                                        <div className="p-2 bg-green-50 rounded border-l-4 border-green-400">
                                          <div className="font-medium text-green-800">Listing Agent</div>
                                          <div className="text-sm text-green-700">
                                            <div><span className="font-medium">Name:</span> {property.details.lastSellingAgent.name}</div>
                                            {property.details.lastSellingAgent.broker && (
                                              <div><span className="font-medium">Brokerage:</span> {property.details.lastSellingAgent.broker}</div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {property.details?.lastBuyersAgent?.name && (
                                        <div className="p-2 bg-blue-50 rounded border-l-4 border-blue-400">
                                          <div className="font-medium text-blue-800">Buyer's Agent</div>
                                          <div className="text-sm text-blue-700">
                                            <div><span className="font-medium">Name:</span> {property.details.lastBuyersAgent.name}</div>
                                            {property.details.lastBuyersAgent.broker && (
                                              <div><span className="font-medium">Brokerage:</span> {property.details.lastBuyersAgent.broker}</div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {!property.details?.lastSellingAgent?.name && !property.details?.lastBuyersAgent?.name && (
                                        <div className="text-sm text-gray-500">No agent information available</div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Artwork Tab */}
              {homesActiveTab === 'artwork' && (
                <div>
                  {generatedArtwork.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No artwork generated yet. Go to the "Agents Assigned" tab, select properties, and click "Generate Artwork" to create watercolor illustrations.
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                      <div className="divide-y divide-gray-200">
                        {generatedArtwork.map((property: any, index: number) => (
                          <div key={index} className="p-4">
                            <div className="flex items-start gap-4">
                              {/* Original Property Image */}
                              {property.details?.primaryImageUrl ? (
                                <div className="flex-shrink-0">
                                  <div className="text-xs text-gray-500 mb-1">Original</div>
                                  <img
                                    src={property.details.primaryImageUrl}
                                    alt={`Original Property ${index + 1}`}
                                    className="w-32 h-32 rounded-lg object-cover"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <span className="text-gray-400 text-xs">No Image</span>
                                </div>
                              )}

                              {/* Generated Artwork */}
                              {property.artworkGenerated && property.artworkUrl ? (
                                <div className="flex-shrink-0">
                                  <div className="text-xs text-green-600 font-medium mb-1">Watercolor Artwork</div>
                                  <img
                                    src={property.artworkUrl}
                                    alt={`Artwork for Property ${index + 1}`}
                                    className="w-32 h-32 rounded-lg object-cover border-2 border-green-200"
                                  />
                                </div>
                              ) : property.artworkError ? (
                                <div className="flex-shrink-0">
                                  <div className="text-xs text-red-600 font-medium mb-1">Artwork Error</div>
                                  <div className="w-32 h-32 bg-red-50 rounded-lg flex items-center justify-center border-2 border-red-200">
                                    <span className="text-red-400 text-xs text-center p-2">{property.artworkError}</span>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex-shrink-0">
                                  <div className="text-xs text-gray-500 mb-1">No Artwork</div>
                                  <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <span className="text-gray-400 text-xs">Generating...</span>
                                  </div>
                                </div>
                              )}
                              
                              {/* Property Details */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="text-sm font-medium text-gray-900">
                                    Property {index + 1}
                                  </div>
                                  {property.details?.dateSold && (
                                    <div className="text-sm text-gray-500">
                                      Sold: {property.details.dateSold}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Address Used for Artwork Generation */}
                                <div className="mb-3 p-2 bg-purple-50 rounded text-sm">
                                  <div className="font-medium text-purple-800 mb-1">Address Used for Artwork</div>
                                  <div className="text-purple-700">
                                    {(() => {
                                      // Reconstruct the same address logic used in handleGenerateArtwork
                                      if (property.details?.streetNumber && property.details?.streetName) {
                                        let addr = `${property.details.streetNumber} ${property.details.streetName}`;
                                        const locationParts = [];
                                        if (property.details.city) locationParts.push(property.details.city);
                                        if (property.details.state) locationParts.push(property.details.state);
                                        if (property.details.zipCode) locationParts.push(property.details.zipCode);
                                        if (locationParts.length > 0) {
                                          addr += `, ${locationParts.join(', ')}`;
                                        }
                                        return addr;
                                      } else if (property.details?.fullStreetAddress) {
                                        let addr = property.details.fullStreetAddress;
                                        const locationParts = [];
                                        if (property.details.city && !addr.includes(property.details.city)) {
                                          locationParts.push(property.details.city);
                                        }
                                        if (property.details.state && !addr.includes(property.details.state)) {
                                          locationParts.push(property.details.state);
                                        }
                                        if (property.details.zipCode && !addr.includes(property.details.zipCode)) {
                                          locationParts.push(property.details.zipCode);
                                        }
                                        if (locationParts.length > 0) {
                                          addr += `, ${locationParts.join(', ')}`;
                                        }
                                        return addr;
                                      } else {
                                        return property.href || 'Property address not available';
                                      }
                                    })()}
                                  </div>
                                  {property.artworkStyle && (
                                    <div className="text-purple-600 text-xs mt-1">
                                      Style: {property.artworkStyle} • Generated via AI
                                    </div>
                                  )}
                                  {property.artworkRequestId && (
                                    <div className="text-purple-500 text-xs">
                                      Request ID: {property.artworkRequestId}
                                    </div>
                                  )}
                                </div>
                                
                                <div className="text-sm text-gray-600 mb-3">
                                  {property.details?.url ? (
                                    <a 
                                      href={property.details.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 underline"
                                    >
                                      View Property →
                                    </a>
                                  ) : (
                                    <span>Path: {property.href}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Zillow Agent Info Collector */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Collect Buyer's Agent Info</h3>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label htmlFor="zillow-url" className="block text-sm font-medium text-gray-700 mb-2">
                Zillow Listing URL
              </label>
              <input
                type="url"
                id="zillow-url"
                value={zillowUrl}
                onChange={(e) => setZillowUrl(e.target.value)}
                placeholder="https://www.zillow.com/homedetails/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={scrapingLoading}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleCollectAgentInfo}
                disabled={scrapingLoading || !zillowUrl.trim()}
                className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {scrapingLoading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                {scrapingLoading ? 'Collecting...' : 'Collect Info'}
              </button>
            </div>
          </div>
          
          {scrapingError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{scrapingError}</p>
            </div>
          )}

          {agentInfo && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-semibold text-green-800 mb-3">Buyer's Agent Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Property Address:</span>
                  <p className="text-gray-900">{agentInfo.street_address || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Location:</span>
                  <p className="text-gray-900">
                    {[agentInfo.city, agentInfo.state, agentInfo.zip].filter(Boolean).join(', ') || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Sold Date:</span>
                  <p className="text-gray-900">{agentInfo.sold_date || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Agent Name:</span>
                  <p className="text-gray-900">{agentInfo.buyer_agent_name || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Agent License:</span>
                  <p className="text-gray-900">{agentInfo.buyer_agent_license || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Brokerage:</span>
                  <p className="text-gray-900">{agentInfo.buyer_brokerage || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Phone (Zillow):</span>
                  <p className="text-gray-900">
                    {agentInfo.buyer_agent_phone ? (
                      <a href={`tel:${agentInfo.buyer_agent_phone}`} className="text-indigo-600 hover:text-indigo-800">
                        {agentInfo.buyer_agent_phone}
                      </a>
                    ) : 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">AI Found Email:</span>
                  <p className="text-gray-900">
                    {agentInfo.ai_found_email ? (
                      <a href={`mailto:${agentInfo.ai_found_email}`} className="text-indigo-600 hover:text-indigo-800">
                        {agentInfo.ai_found_email}
                      </a>
                    ) : 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">AI Found Mobile:</span>
                  <p className="text-gray-900">
                    {agentInfo.ai_found_mobile ? (
                      <a href={`tel:${agentInfo.ai_found_mobile}`} className="text-indigo-600 hover:text-indigo-800">
                        {agentInfo.ai_found_mobile}
                      </a>
                    ) : 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Search Status:</span>
                  <p className="text-gray-900">
                    {agentInfo.search_method === 'web_search' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                        Found via Web Search
                      </span>
                    )}
                    {agentInfo.search_method === 'standard_search' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                        Found via Standard Search
                      </span>
                    )}
                    {agentInfo.search_method === 'failed_parse' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                        Parse Failed
                      </span>
                    )}
                    {agentInfo.search_method === 'api_failed' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                        API Failed
                      </span>
                    )}
                    {agentInfo.search_method === 'error' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                        Search Error
                      </span>
                    )}
                    {agentInfo.search_method === 'no_agent_name' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                        No Agent Name Found
                      </span>
                    )}
                    {agentInfo.search_method === 'no_api_key' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                        No API Key
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Recent Properties</p>
                <p className="text-2xl font-bold text-gray-900">{recentProperties.length}</p>
              </div>
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Older Properties</p>
                <p className="text-2xl font-bold text-gray-900">{olderProperties.length}</p>
              </div>
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Pages</p>
                <p className="text-2xl font-bold text-gray-900">{totalPages}</p>
              </div>
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <div className="w-4 h-4 bg-purple-500 rounded-full"></div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Results</p>
                <p className="text-2xl font-bold text-gray-900">{totalResults.toLocaleString()}</p>
              </div>
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <MapPin className="w-4 h-4 text-indigo-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Property Lists */}
        {(recentProperties.length > 0 || olderProperties.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Properties List */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Recent Properties ({recentProperties.length})
                    </h3>
                    <p className="text-sm text-gray-500">Most recently sold properties</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="select-all-recent"
                      checked={recentProperties.length > 0 && recentProperties.every(p => selectedProperties.has(p.id))}
                      onChange={(e) => handleSelectAllProperties(recentProperties, e.target.checked)}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <label htmlFor="select-all-recent" className="text-sm text-gray-700">
                      Select All
                    </label>
                  </div>
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {recentProperties.map((property, index) => (
                  <div key={`recent-${property.id}-${index}`} className="p-4 border-b border-gray-100 hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <input
                        type="checkbox"
                        id={`recent-${property.id}`}
                        checked={selectedProperties.has(property.id)}
                        onChange={(e) => handlePropertySelection(property.id, e.target.checked)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 flex-shrink-0"
                      />
                      {property.images && property.images.length > 0 && (
                        <img
                          src={property.images[0]}
                          alt="Property"
                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {property.address}
                        </div>
                        <div className="text-sm text-gray-500">
                          ${property.price}
                        </div>
                        <div className="text-sm text-gray-500">
                          Sold: {property.soldDate || 'N/A'}
                        </div>
                        <a
                          href={property.listingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-indigo-600 hover:text-indigo-800 truncate block"
                        >
                          View Listing →
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Older Properties List */}
            {olderProperties.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Older Properties ({olderProperties.length})
                      </h3>
                      <p className="text-sm text-gray-500">Properties sold approximately a year ago</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="select-all-older"
                        checked={olderProperties.length > 0 && olderProperties.every(p => selectedProperties.has(p.id))}
                        onChange={(e) => handleSelectAllProperties(olderProperties, e.target.checked)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <label htmlFor="select-all-older" className="text-sm text-gray-700">
                        Select All
                      </label>
                    </div>
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {olderProperties.map((property, index) => (
                    <div key={`older-${property.id}-${index}`} className="p-4 border-b border-gray-100 hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <input
                          type="checkbox"
                          id={`older-${property.id}`}
                          checked={selectedProperties.has(property.id)}
                          onChange={(e) => handlePropertySelection(property.id, e.target.checked)}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 flex-shrink-0"
                        />
                        {property.images && property.images.length > 0 && (
                          <img
                            src={property.images[0]}
                            alt="Property"
                            className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {property.address}
                          </div>
                          <div className="text-sm text-gray-500">
                            ${property.price}
                          </div>
                          <div className="text-sm text-gray-500">
                            Sold: {property.soldDate || 'N/A'}
                          </div>
                          <a
                            href={property.listingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-indigo-600 hover:text-indigo-800 truncate block"
                          >
                            View Listing →
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {recentProperties.length === 0 && olderProperties.length === 0 && !soldPropertiesLoading && (
          <div className="text-center py-12">
            <div className="text-gray-500">No properties found. Try searching for a different area.</div>
          </div>
        )}

        {/* Agent Results Table */}
        {foundAgents.length > 0 && (
          <div className="mt-8 bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Found Buyer Agents ({foundAgents.length})
              </h3>
              <p className="text-sm text-gray-500">Agent information for selected properties</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Property
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sold Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Buyer Agent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {foundAgents.map((agent, index) => (
                    <tr key={`agent-${agent.id}-${index}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {agent.address}
                        </div>
                        <div className="text-sm text-gray-500">
                          ${agent.price}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.soldDate || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {agent.images && agent.images.length > 0 ? (
                          <img
                            src={agent.images[0]}
                            alt="Property"
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                            <span className="text-gray-400 text-xs">No Image</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {agent.agentInfo?.buyer_agent_name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {agent.agentInfo?.buyer_agent_license || 'No license'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {agent.agentInfo?.buyer_brokerage || 'No brokerage'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {agent.agentInfo?.buyer_agent_phone && (
                            <div className="text-sm">
                              <span className="text-gray-500">Phone: </span>
                              <a
                                href={`tel:${agent.agentInfo.buyer_agent_phone}`}
                                className="text-indigo-600 hover:text-indigo-800"
                              >
                                {agent.agentInfo.buyer_agent_phone}
                              </a>
                            </div>
                          )}
                          {agent.agentInfo?.ai_found_email && (
                            <div className="text-sm">
                              <span className="text-gray-500">Email: </span>
                              <a
                                href={`mailto:${agent.agentInfo.ai_found_email}`}
                                className="text-indigo-600 hover:text-indigo-800"
                              >
                                {agent.agentInfo.ai_found_email}
                              </a>
                            </div>
                          )}
                          {agent.agentInfo?.ai_found_mobile && (
                            <div className="text-sm">
                              <span className="text-gray-500">Mobile: </span>
                              <a
                                href={`tel:${agent.agentInfo.ai_found_mobile}`}
                                className="text-indigo-600 hover:text-indigo-800"
                              >
                                {agent.agentInfo.ai_found_mobile}
                              </a>
                            </div>
                          )}
                          {!agent.agentInfo?.buyer_agent_phone && !agent.agentInfo?.ai_found_email && !agent.agentInfo?.ai_found_mobile && (
                            <div className="text-sm text-gray-500">No contact info</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {agent.agentInfo?.search_error ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                            Error
                          </span>
                        ) : agent.agentInfo?.search_method === 'web_search' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                            Web Search
                          </span>
                        ) : agent.agentInfo?.search_method === 'standard_search' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                            Standard
                          </span>
                        ) : agent.agentInfo?.buyer_agent_name ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                            Found
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                            No Data
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBuyerOutreach; 